'use strict';
import _ from 'lodash';
import c from 'common/constants/flux-events';
import { createServerErrorAsToastr } from 'common/server-errors-toastr';
import AdPresetActions from 'states/resources/ad-presets/actions';
import { createHttp } from 'utils/http';

import UserActions from 'states/resources/users/actions';

const useGraphQLToGetOwnUserAndOwnOrganization = () => {
    const http = createHttp();
    const query = `
        query getUserAndOrganizationDataForProfilePage {
            ownUser {
                id
                first_name
                last_name
                email
                global_role
                suspended
                third_party
                organization
                _etag
                subscribedToEmailNotifications
                preferences {
                    useNewReportTable
                }
            }
            ownOrganization {
                name
                id
            }
        }
    `;
    return http.graphql(query, {});
};

export function initProfile(orgId) {
    return async dispatch => {
        try {
            const data = await useGraphQLToGetOwnUserAndOwnOrganization();
            // Keep legacy behavior: once profile user/org is loaded successfully,
            // return early and skip the ad presets fetch in this flow.
            return dispatch({
                type: 'PROFILE_USER__FETCH_SUCCESS',
                payload: { user: data.ownUser, organization: data.ownOrganization },
            });
        } catch (err) {
            dispatch({
                type: 'PROFILE_USER__FETCH_FAIL',
                payload: {
                    err: {
                        message: err,
                    },
                },
            });
        }

        dispatch(AdPresetActions.getAll(orgId)).then(
            adpresets => {
                dispatch({
                    type: c.PROFILE_AD_PRESETS_FETCH,
                    payload: {
                        adpresets,
                        orgId,
                    },
                });
            },
            error => {
                dispatch({
                    type: c.PROFILE_AD_PRESETS_FETCH_FAIL,
                    error: error.body,
                });
            }
        );
    };
}

export const updateSubscriptionPreferences = ({ shouldSubscribeToEmail }) => {
    return async (dispatch, getState) => {
        const userId = _.get(getState(), 'profilePage.user.id');
        const userEtag = _.get(getState(), 'profilePage.user.etag');
        const payload = { subscribedToEmailNotifications: shouldSubscribeToEmail };

        dispatch({
            type: 'PROFILE_PAGE__UPDATE_EMAIL_SUBSCRIPTION_PREFERENCES_START',
            payload: {},
        });

        try {
            const user = await dispatch(UserActions.update(userId, payload, userEtag));
            dispatch({
                type: 'PROFILE_PAGE__UPDATE_EMAIL_SUBSCRIPTION_PREFERENCES_SUCCESS',
                payload: { user },
            });
            dispatch({
                type: 'PROFILE__USER_PREFERENCES_NOTIFICATIONS_LAST_SEEN__UPDATE',
                payload: { user },
            });
        } catch (error) {
            dispatch({
                type: 'PROFILE_PAGE__UPDATE_EMAIL_SUBSCRIPTION_PREFERENCES_FAIL',
                payload: {},
            });
        }
    };
};

/**
 * Saves `preferences.useNewReportTable` for the signed-in user, including the
 * impersonated user. Does not skip impersonation.
 *
 * @param {{ enabled: boolean }} args
 * @returns {function(Function, Function): Promise<void>}
 */
export const updateNewReportingPreference = ({ enabled }) => {
    return async (dispatch, getState) => {
        const userId = _.get(getState(), 'profilePage.user.id');
        const userEtag = _.get(getState(), 'profilePage.user.etag');
        const payload = {
            preferences: {
                useNewReportTable: enabled === true,
            },
        };

        dispatch({
            type: 'PROFILE_PAGE__UPDATE_NEW_REPORTING_PREFERENCE_START',
            payload: {},
        });

        try {
            const user = await dispatch(UserActions.update(userId, payload, userEtag));

            dispatch({
                type: 'PROFILE_PAGE__UPDATE_NEW_REPORTING_PREFERENCE_SUCCESS',
                payload: { user },
            });
            dispatch({
                type: 'PROFILE__USER_PREFERENCES_NOTIFICATIONS_LAST_SEEN__UPDATE',
                payload: { user },
            });
        } catch (error) {
            createServerErrorAsToastr(error);
            dispatch({
                type: 'PROFILE_PAGE__UPDATE_NEW_REPORTING_PREFERENCE_FAIL',
                payload: {},
            });
        }
    };
};
